/**
 * The screens notice what changes on the platform.
 *
 * Every figure on a screen is a query with a stale time — fifteen seconds for
 * work orders, a minute for the KPIs — and a change made by somebody else, or
 * by the field app, or through the API, reached this browser only when a
 * query happened to expire and be asked for again. Followed through in the
 * platform-wide check: a work order created by the API was on the platform,
 * and Sky counted it, while the list and the dashboard tile stood at the old
 * figure until the next visit outside the stale window. Nothing on screen was
 * wrong; it was old, with nothing to say so.
 *
 * The platform keeps a gap-free feed of what changed (`/v1/events`, with a
 * sequence cursor). This polls it every ten seconds while the tab is visible,
 * and when anything has happened since the last look, marks every query stale
 * — React Query then refetches the ones on screen and leaves the rest until
 * they are wanted. One cheap call a tick, a refetch only when there is
 * something to refetch, and nothing pushed at a tab nobody is looking at.
 *
 * It is not a live socket. Ten seconds is the promise, and the sidebar says
 * "live" only while the last poll succeeded.
 */
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { DataSource } from "../api/dataSource";
import { requestRepaint } from "../demo/lib/repaint";
import { useSession } from "./SessionContext";

export const POLL_MS = 10_000;

export interface LiveStatus {
  /** the platform feed is being watched and the last look succeeded */
  live: boolean;
  /** why not, when not */
  reason: string | null;
  /** when something last changed and the screens were refreshed */
  lastChangeAt: number | null;
}

const Ctx = createContext<LiveStatus>({ live: false, reason: "not started", lastChangeAt: null });

export function useLiveRefresh(): LiveStatus {
  return useContext(Ctx);
}

/**
 * Where the feed stands now. Read from the oldest page forward, because the
 * feed has no "latest" — a one-time cost bounded by the platform's own
 * pruning of old events.
 */
async function findTail(source: Pick<DataSource, "pollEvents">, siteId: string): Promise<number> {
  let cursor = 0;
  for (let i = 0; i < 50; i++) {
    const page = await source.pollEvents(siteId, cursor, 1000);
    cursor = page.cursor;
    if (!page.more) break;
  }
  return cursor;
}

export function LiveRefreshProvider({
  children,
  source: override,
  pollMs = POLL_MS,
}: {
  children: ReactNode;
  /** a feed other than the session's — for a test */
  source?: DataSource;
  pollMs?: number;
}) {
  const session = useSession();
  const source = override ?? session.source;
  const siteId = session.site.id;
  const qc = useQueryClient();
  const [polled, setStatus] = useState<LiveStatus>({ live: false, reason: "starting", lastChangeAt: null });
  const cursor = useRef<number | null>(null);
  /* derived, not set: the sample has no feed to watch, and that is known at render */
  const status: LiveStatus =
    source.kind === "platform"
      ? polled
      : { live: false, reason: "the built-in sample does not change", lastChangeAt: null };

  useEffect(() => {
    if (source.kind !== "platform") return;
    let stopped = false;
    let timer: number | null = null;
    cursor.current = null;

    const tick = async () => {
      if (stopped) return;
      if (document.visibilityState === "hidden") {
        schedule();
        return;
      }
      try {
        if (cursor.current === null) {
          cursor.current = await findTail(source, siteId);
          if (stopped) return;
          setStatus((st) => ({ ...st, live: true, reason: null }));
        } else {
          const page = await source.pollEvents(siteId, cursor.current, 200);
          if (stopped) return;
          if (page.events.length > 0) {
            cursor.current = page.cursor;
            /* everything on screen asks again; everything else waits until it is wanted */
            await qc.invalidateQueries();
            requestRepaint();
            setStatus({ live: true, reason: null, lastChangeAt: Date.now() });
          } else {
            setStatus((st) => (st.live && !st.reason ? st : { ...st, live: true, reason: null }));
          }
        }
      } catch (e) {
        /* a cursor that fell behind the platform's pruning starts again from the tail */
        if (e instanceof Error && /cursor_behind_horizon/.test(e.message)) cursor.current = null;
        setStatus((st) => ({ ...st, live: false, reason: e instanceof Error ? e.message : String(e) }));
      }
      schedule();
    };
    const schedule = () => {
      if (!stopped) timer = window.setTimeout(() => void tick(), pollMs);
    };
    /* a tab coming back into view looks straight away rather than waiting out the interval */
    const onVisible = () => {
      if (document.visibilityState === "visible" && timer !== null) {
        window.clearTimeout(timer);
        timer = null;
        void tick();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    void tick();
    return () => {
      stopped = true;
      if (timer !== null) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [pollMs, qc, siteId, source]);

  return <Ctx.Provider value={status}>{children}</Ctx.Provider>;
}
